import { Grid, Card, CardHeader, CardContent, Divider, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { TrendingUp, Savings, Percent, Insights } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { MiniBarChart } from '../../components/MiniBarChart';
import { StatusBadge } from '../../components/StatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { profitabilityApi } from '../../api/profitability.api';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function ProfitabilityDashboardPage() {
  const year = new Date().getFullYear();
  const summary = useQuery({ queryKey: ['prof-summary'], queryFn: () => profitabilityApi.summary() });
  const trends = useQuery({ queryKey: ['prof-trends', year], queryFn: () => profitabilityApi.trends(year) });
  const byClient = useQuery({ queryKey: ['prof-by-client'], queryFn: () => profitabilityApi.byDimension('client') });
  const perf = useQuery({ queryKey: ['prof-perf'], queryFn: () => profitabilityApi.performance() });
  const forecast = useQuery({ queryKey: ['prof-forecast'], queryFn: () => profitabilityApi.forecast() });

  const revenueData = (trends.data?.revenueTrend ?? []).map((t) => ({ label: MONTHS[t.month - 1], value: t.value, color: '#1e40af' }));
  const marginData = (trends.data?.marginTrend ?? []).map((t) => ({ label: MONTHS[t.month - 1], value: t.value, color: '#0d9488' }));

  return (
    <>
      <PageHeader title="Business Intelligence" subtitle="Profitability, margins and performance across the portfolio." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#1e40af" label="Revenue" value={inr(summary.data?.revenue)} icon={<TrendingUp />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#16a34a" label="Net Profit" value={inr(summary.data?.netProfit)} icon={<Savings />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#7c3aed" label="Net Margin" value={`${summary.data?.netMarginPct ?? 0}%`} icon={<Percent />} hint={summary.data?.status?.replace(/_/g, ' ')} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#0d9488" label="Revenue Forecast" value={inr(forecast.data?.revenueForecast)} icon={<Insights />} hint={`Collection ${inr(forecast.data?.collectionForecast)}`} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><Card sx={{ height: '100%' }}><CardHeader title="Revenue Trend" titleTypographyProps={{ variant: 'subtitle1' }} /><CardContent>{trends.data ? <MiniBarChart data={revenueData} valueFormatter={inr} /> : <EmptyState title="Loading…" dense />}</CardContent></Card></Grid>
        <Grid item xs={12} md={6}><Card sx={{ height: '100%' }}><CardHeader title="Net Margin Trend (%)" titleTypographyProps={{ variant: 'subtitle1' }} /><CardContent>{trends.data ? <MiniBarChart data={marginData} valueFormatter={(v) => `${v}%`} /> : <EmptyState title="Loading…" dense />}</CardContent></Card></Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader title="Profitability by Client" titleTypographyProps={{ variant: 'subtitle1' }} />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Client</TableCell><TableCell align="right">Revenue</TableCell><TableCell align="right">Net Profit</TableCell><TableCell align="right">Margin</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                <TableBody>
                  {(byClient.data ?? []).length === 0 && <TableRow><TableCell colSpan={5}><EmptyState title="No data yet" dense /></TableCell></TableRow>}
                  {(byClient.data ?? []).map((r) => (
                    <TableRow key={r.key} hover>
                      <TableCell>#{r.key}</TableCell><TableCell align="right">{inr(r.revenue)}</TableCell>
                      <TableCell align="right">{inr(r.netProfit)}</TableCell><TableCell align="right">{r.netMarginPct}%</TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardHeader title="Vendor Performance" titleTypographyProps={{ variant: 'subtitle1' }} />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Vendor</TableCell><TableCell align="right">Order Value</TableCell></TableRow></TableHead>
                <TableBody>
                  {(perf.data?.vendorPerformance ?? []).length === 0 && <TableRow><TableCell colSpan={2}><EmptyState title="No data yet" dense /></TableCell></TableRow>}
                  {(perf.data?.vendorPerformance ?? []).map((v) => (
                    <TableRow key={v.vendorId} hover><TableCell>#{v.vendorId}</TableCell><TableCell align="right">{inr(v.value)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
